import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../services/firebase';
import { hasProductAccess } from './accessUtils';

/** Conteúdo do tipo Link (`item.url`), alinhado ao PlayList e ao setTypeContent do mobile. */
export function isLinkContent(item) {
  if (!item) return false;

  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (!url) return false;

  if (item.type === 'live' || item.type === 'livestream') return false;

  const hasPdf =
    (typeof item?.pdf === 'string' && item.pdf.trim() !== '') ||
    (Array.isArray(item?.pdf) && item.pdf.length > 0) ||
    (typeof item?.pdf === 'object' &&
      item.pdf !== null &&
      !Array.isArray(item.pdf) &&
      Object.keys(item.pdf).length > 0);

  if (hasPdf) return false;

  const videoExtensions = ['mp4', 'mov', 'm4v'];
  const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a'];
  const extension =
    item?.videoInfo?.metadata?.extension
      ? `${item.videoInfo.metadata.extension}`.toLowerCase()
      : item?.videoInfo?.metadata?.title
        ? item.videoInfo.metadata.title
            .substring(item.videoInfo.metadata.title.lastIndexOf('.') + 1)
            .toLowerCase()
        : item?.pandaVideoMetadata?.title
          ? item.pandaVideoMetadata.title
              .substring(item.pandaVideoMetadata.title.lastIndexOf('.') + 1)
              .toLowerCase()
          : '';

  if (extension) {
    if (videoExtensions.includes(extension) || item.link) return false;
    if (audioExtensions.includes(extension)) return false;
  } else if (
    item.link ||
    item?.videoInfo?.storage === 'firebase' ||
    item?.videoInfo?.storage === 'livePanda'
  ) {
    return false;
  }

  return true;
}

export function resolveLinkContentUrl(item, context = {}) {
  const url = typeof item?.url === 'string' ? item.url.trim() : '';
  if (!url) return null;

  const { webUrl, categoryId, cardId, videoId } = context;
  if (url.includes('pandavideo') && webUrl && categoryId && cardId && videoId) {
    return `${webUrl}/Watch?categoryId=${categoryId}&cardId=${cardId}&selectedVideo=${videoId}`;
  }

  return url;
}

export function openLinkContent(item, context = {}) {
  const target = resolveLinkContentUrl(item, context);
  if (!target) return false;

  window.open(target, '_blank');
  return true;
}

async function fetchVideoContent(categoryId, cardId, videoId) {
  try {
    const ref = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      videoId
    );
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch {
    return null;
  }
}

async function resolveContentItem(params) {
  const { item, categoryId, cardId, videoId } = params;

  if (item && isLinkContent(item)) return item;

  const resolvedVideoId = videoId ?? item?.id ?? item?.videoId ?? item?.docId;
  const resolvedCategoryId = categoryId ?? item?.categoryId;
  const resolvedCardId = cardId ?? item?.cardId;

  if (resolvedVideoId && resolvedCategoryId && resolvedCardId) {
    const fetched = await fetchVideoContent(
      resolvedCategoryId,
      resolvedCardId,
      resolvedVideoId
    );
    if (fetched) return fetched;
  }

  return item ?? null;
}

/**
 * The external URL is the content itself, so it must only open for students
 * with access to the module (favorites, notifications and deep links reach
 * here without the lock check the module screen does). Uses the unified
 * hasProductAccess engine; on any read failure, deny and let the Aula screen
 * decide.
 */
async function userCanOpenModuleLink(categoryId, cardId) {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid || !categoryId || !cardId) return false;

    const [cardSnap, userSnap] = await Promise.all([
      getDoc(doc(firestore, 'categories', categoryId, 'cards', cardId)),
      getDoc(doc(firestore, 'users', uid)),
    ]);
    if (!cardSnap.exists()) return false;

    return hasProductAccess(cardSnap.data(), userSnap.exists() ? userSnap.data() : null);
  } catch {
    return false;
  }
}

/** Abre link externo direto ou delega para `onOpenClass` (navegação à Aula). */
export async function openClassOrLinkContent(params) {
  const content = await resolveContentItem(params);
  const videoId =
    params.videoId ?? content?.id ?? content?.docId ?? params.item?.videoId;
  const categoryId = params.categoryId ?? content?.categoryId ?? params.item?.categoryId;
  const cardId = params.cardId ?? content?.cardId ?? params.item?.cardId;

  if (content && isLinkContent(content) && (await userCanOpenModuleLink(categoryId, cardId))) {
    params.onOpenClass();
    await new Promise((resolve) => setTimeout(resolve, 400));
    openLinkContent(content, {
      webUrl: params.webUrl,
      categoryId,
      cardId,
      videoId,
    });
    return;
  }

  params.onOpenClass();
}
